// src/utils/permissions.js
// Einfache Rollenverwaltung für Azure AD Integration

export const ROLES = {
  ADMIN: 'MF-Admin',
  PL: 'MF-PL',
  TEAM: 'MF-Team',
  FREELANCER: 'MF-Freelancer',
  // Externe Person mit ausschliesslich Zugriff auf das El-Gato-Muerto-Tool
  ELGATO: 'MF-ElGatoMuerto'
};

// Benutzerrolle prüfen
export const hasRole = (user, role) => {
  if (!user?.userRoles) return false;
  return user.userRoles.includes(role);
};

// Hierarchische Rollenverwaltung
export const hasMinimumRole = (user, minimumRole) => {
  if (!user?.userRoles) return false;

  const roleHierarchy = {
    [ROLES.ADMIN]: 3,
    [ROLES.PL]: 2,
    [ROLES.TEAM]: 1,
    [ROLES.FREELANCER]: 0,
    [ROLES.ELGATO]: 0
  };

  const userLevel = Math.max(...user.userRoles.map(role => roleHierarchy[role] || 0));
  const requiredLevel = roleHierarchy[minimumRole] || 0;

  return userLevel >= requiredLevel;
};

// Check if user is a freelancer
export const isFreelancer = (user) => {
  if (!user?.userRoles) return false;
  return user.userRoles.includes(ROLES.FREELANCER) &&
         !user.userRoles.includes(ROLES.ADMIN) &&
         !user.userRoles.includes(ROLES.PL) &&
         !user.userRoles.includes(ROLES.TEAM);
};

// Externe El-Gato-Muerto-Person: darf ausschliesslich das Website-/El-Gato-Tool
// sehen und dort nur die El-Gato-Muerto-Felder bearbeiten. Keine andere Rolle.
export const isElGatoOnly = (user) => {
  if (!user?.userRoles) return false;
  return user.userRoles.includes(ROLES.ELGATO) &&
         !user.userRoles.includes(ROLES.ADMIN) &&
         !user.userRoles.includes(ROLES.PL) &&
         !user.userRoles.includes(ROLES.TEAM) &&
         !user.userRoles.includes(ROLES.FREELANCER);
};

// Feature-basierte Berechtigungen
export const canAccess = (user, feature) => {
  const permissions = {
    // Bestehende Features
    zeiterfassung: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    projectOverview: [ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    ressourcenplanung: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    finanzen: [ROLES.ADMIN],
    config: [ROLES.ADMIN],
    projekte: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    finanzcheck: [ROLES.ADMIN],
    crm: [ROLES.ADMIN, ROLES.PL],
    team: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    // ELGATO ist die EINZIGE Berechtigung der externen El-Gato-Person —
    // bewusst in keinem anderen Feature gelistet.
    website: [ROLES.ADMIN, ROLES.PL, ROLES.ELGATO],
    references: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Research-/Q&A-Tool (Sakralbauten-Buch): sichtbar für MF-Team aufwärts
    research: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    userManagement: [ROLES.ADMIN, ROLES.PL],
    systemSettings: [ROLES.ADMIN],
    // BKP-Kostenschaetzung - Freelancers can edit
    bkpKostenschaetzung: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Kostenschätzungs-Dossiers - alle Rollen sichtbar
    kostenschaetzung: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Terminpläne (Gantt) - Liste/Ansicht für alle angemeldeten Rollen inkl. Freelancer.
    // (ELGATO bleibt bewusst aussen vor – isolierter El-Gato-Zugang, siehe isElGatoOnly.)
    termine: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Pendenzen/Entscheide/Notizen pro Projekt - internes Team (Admin/PL/Team),
    // bewusst ohne Freelancer (US-PEND-001).
    pendenzen: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    // Projektübergreifende Ansicht "Meine Pendenzen" - alle angemeldeten Rollen
    // inkl. Freelancer (diese sehen gefiltert nur ihre zugewiesenen Projekte, US-PEND-005).
    meinePendenzen: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Pläne (Grundrisse/Pläne hochladen, ansehen, verorten) pro Projekt - internes
    // Team (Admin/PL/Team), bewusst ohne Freelancer (US-PLAN-001, analog Pendenzen).
    plaene: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    // Sitzungsgefässe (Jour fix etc.) pro Projekt - internes Team (US-SITZ-001).
    sitzungen: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    // Externe Projektwebsite pro Projekt (Reiter "Website") - internes Team
    // (Admin/PL/Team), bewusst ohne Freelancer, analog Pendenzen/Pläne/Sitzungen.
    // Der Reiter erscheint zusätzlich nur, wenn GL ihn für das Projekt aktiviert
    // hat (Projekt-Property Projektwebsite, US-WEBP-001).
    projektwebsite: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM]
  };

  if (!permissions[feature]) return false;
  return permissions[feature].some(role => hasRole(user, role));
};

// Darf der/die zugeordnete Nutzer:in projektbezogene Inhalte bearbeiten?
// Admin/PL dürfen immer; sonst nur, wenn die eigene employeeUuid dem Projekt
// zugeordnet ist (Tabelle projectEmployeeAssignments). Diese Zuordnung ist über
// alle Projekt-Werkzeuge gemeinsam (Controlling, Pendenzen, Termine, Kosten-
// schätzung), daher sind die Schreibrechte dort synchron: Wer die Pendenzenliste
// eines Projekts bearbeiten darf, darf automatisch auch dessen Kostenschätzung
// bearbeiten (ADR 0020). Erwartet `assignments` = Array aus
// getProjectAssignments() mit Feld `employeeUuid`.
export const canEditProjectAssigned = (user, assignments) => {
  if (hasRole(user, ROLES.ADMIN) || hasRole(user, ROLES.PL)) return true;
  const uuid = user?.teamMember?.employeeUuid;
  return (
    !!uuid &&
    Array.isArray(assignments) &&
    assignments.some((a) => a.employeeUuid === uuid)
  );
};

// Prüfe ob der Benutzer localOnly setzen darf (nur GL/Admin)
export const canSetLocalOnly = (user) => {
  return hasRole(user, ROLES.ADMIN);
};

// Darf der/die Nutzer:in entscheiden, dass ein Projekt eine externe Projektwebsite
// erhält (Schalter im Projekt-Bearbeitungsformular)? Bewusst nur GL/Admin — PL sehen
// den Schalter, können ihn aber nicht umstellen (US-WEBP-001, ADR 0045). Das
// BEARBEITEN der Website-Inhalte richtet sich dagegen nach canEditProjectAssigned.
export const canToggleProjectWebsite = (user) => {
  return hasRole(user, ROLES.ADMIN);
};

// Feature-basierte Bearbeitungsrechte (nur Admin und PL können bearbeiten)
export const canEdit = (user, feature) => {
  const editPermissions = {
    ressourcenplanung: [ROLES.ADMIN, ROLES.PL],
    projekte: [ROLES.ADMIN, ROLES.PL],
    controlling: [ROLES.ADMIN, ROLES.PL],
    stundenAuszug: [ROLES.ADMIN, ROLES.PL],
    // BKP-Kostenschaetzung - Freelancers can also edit
    bkpKostenschaetzung: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM, ROLES.FREELANCER],
    // Kostenschätzungs-Dossiers erstellen - nur Admin/PL
    kostenschaetzung: [ROLES.ADMIN, ROLES.PL],
    // Projektstand - nur GL (Admin) und PL
    projektstand: [ROLES.ADMIN, ROLES.PL],
    // Research-/Q&A-Tool bearbeiten - gesamtes internes Team (Admin/PL/Team)
    research: [ROLES.ADMIN, ROLES.PL, ROLES.TEAM],
    // Terminpläne bearbeiten/erstellen - nur Admin/PL (Team liest)
    termine: [ROLES.ADMIN, ROLES.PL],
  };

  if (!editPermissions[feature]) return canAccess(user, feature);
  return editPermissions[feature].some(role => hasRole(user, role));
};

// Darf der/die Nutzer:in einen NEUEN Terminplan eröffnen? Das gesamte interne Team
// (GL/PL/Team) darf – Freelancer:innen sehen die Liste nur. Wer einen Plan eröffnet,
// wird beim Anlegen automatisch als Bearbeiter:in eingetragen (siehe TimelineList).
export const canCreateTimeline = (user) =>
  [ROLES.ADMIN, ROLES.PL, ROLES.TEAM].some((r) => hasRole(user, r));

// Darf der/die Nutzer:in den INHALT eines bestimmten Terminplans bearbeiten?
// Admin/PL dürfen immer; sonst nur, wenn das Team-Mitglied für genau diesen Plan
// freigegeben ist (timeline.allowedEditors = [{uuid, teamId, name}]). Matching über
// employeeUuid (kanonisch, ADR 0003) mit teamId als Fallback.
export const canEditTimeline = (user, timeline) => {
  if (canEdit(user, "termine")) return true;
  const list = (timeline && timeline.allowedEditors) || [];
  const tm = user && user.teamMember;
  if (!tm || list.length === 0) return false;
  return list.some(
    (e) =>
      (e.uuid && tm.employeeUuid && e.uuid === tm.employeeUuid) ||
      (e.teamId && tm.teamId && e.teamId === tm.teamId)
  );
};

// Navigation Items filtern
export const getVisibleNavigationItems = (user, allItems) => {
  return allItems.filter(item => {
    if (!item.permission) return true; // Öffentliche Items
    return canAccess(user, item.permission);
  });
};

// Debug-Hilfsfunktion
export const getUserPermissionInfo = (user) => {
  if (!user) return { roles: [], permissions: [] };

  const userRoles = user.userRoles || [];
  const allFeatures = [
    'zeiterfassung', 'projectOverview', 'ressourcenplanung', 'config',
    'projekte', 'finanzen', 'crm', 'team', 'website', 'references', 'research', 'userManagement', 'systemSettings', 'bkpKostenschaetzung'
  ];

  const permissions = allFeatures.filter(feature => canAccess(user, feature));

  return {
    roles: userRoles,
    permissions: permissions,
    isAdmin: hasRole(user, ROLES.ADMIN),
    isPL: hasRole(user, ROLES.PL),
    isTEAM: hasRole(user, ROLES.TEAM),
    isFreelancer: isFreelancer(user)
  };
};